import { v4 as uuidv4 } from "uuid";
import volumeRegistry from "../../Registry/Indicators/volumeRegistry";
import { generateRenderKey } from "../../workspace/core/helpers";

// Cache empty object to prevent infinite re-renders in Zustand selectors
export const EMPTY_INTERACTIVE_OBJECTS = {};

// Helper function to set indicator render key
// Uses shared generateRenderKey from helpers.js to ensure consistency
export const setIndicatorRenderKey = (indicator) => {
  indicator.renderKey = generateRenderKey(indicator.key, indicator.config);
  return indicator;
};

// Helper function to create default volume indicator
const createDefaultVolumeIndicator = () => {
  const volumeIndicator = volumeRegistry.indicators.volume;

  const indicator = {
    key: volumeIndicator.key,
    id: uuidv4(),
    displayName: volumeIndicator.displayName, // Add displayName from registry
    categoryDisplayName: volumeRegistry.displayName, // Add category display name
    config: {
      volumePath: "volume",
    },
    appearanceConfig: {
      upColor: { color: "#6BA583" },
      downColor: { color: "#FF0000" },
    },
    categoryKey: "volume",
    renderKey: "volume",
    // Copy essential registry properties including the type function
    type: volumeIndicator.type,
    requireNewAxis: volumeIndicator.requireNewAxis,
    separatePanel: volumeIndicator.separatePanel,
    yAxisId: volumeIndicator.yAxisId,
    series: volumeIndicator.series,
    coordinate: volumeIndicator.coordinate,
    transformAppearanceConfig: volumeIndicator.transformAppearanceConfig,
    yExtents: volumeIndicator.yExtents,
    tooltip: volumeIndicator.tooltip,
  };

  // Set the render key properly
  return setIndicatorRenderKey(indicator);
};

export const defaultChartConfig = {
  symbol: "WINFUT",
  periodicity: "1min",
  chartType: "candle",
  group: null, // Linking group: null = no group, or 1-5
  barColorProvider: "traditional", // Bar coloring method: 'traditional', 'previousClose', 'volumeComparison', or 'indicator:indicatorKey'
  barColorConfig: {}, // Additional configuration for bar color provider (if needed)
  renkoConfig: {
    // Renko chart configuration
    brickSize: null, // null = auto (~30 bricks), number = fixed size
    engine: "chartiq", // default: motor com paridade HtmlCharts5 (#82); "legacy" = anterior
  },
  pointFigureConfig: {
    // Point & Figure chart configuration (ChartIQ compatible)
    boxSize: null, // null = auto (~30 columns), number = fixed size
    reversal: 3, // boxes needed for reversal
  },
  indicators: [createDefaultVolumeIndicator()],
  isLoading: false,
  error: null,
  // Note: data and dataVersion are now stored in chartData map (see initialChartState)
  // This separation prevents Workspace re-renders on every quote update
  interactiveObjects: {},
  visibleBars: 100, // Default initial visible bars count
  xExtents: null, // Store current chart position/zoom state
  // Newest bar index that `xExtents` (an absolute index domain) is anchored to.
  // Persisted so the viewport can be re-anchored to the last bar after a reload
  // re-bases the array to a fixed window (see rebaseXExtentsForReplace).
  xExtentsLastIndex: null,
  // Hidden chart tracking (Issue #105): Store state when chart becomes hidden
  // to detect new bars and auto-scroll on remount
  lastVisibleBarIndex: null, // Last bar index when chart was visible
  wasLastBarVisible: null, // Whether the last bar was in viewport when hidden
  sectionOrder: [0, 1], // Main chart first, volume indicator second
  sectionHeights: { 0: 0.75, 1: 0.25 }, // Main chart 75%, volume indicator 25% (as per original resize algorithm)
  isLoadingMore: false, // Loading older bars incrementally
  hasMoreData: true, // Can load more historical data
};

export const initialChartState = {
  _initialized: false, // internal flag
  _wasAutoRestored: false, // tracks if workspace was auto-restored on initialization
  charts: [],
  // Separate data storage to prevent Workspace re-renders on every quote
  // Key: chartId, Value: { data: [], dataVersion: 0 }
  chartData: {},
  selectedTab: 0, // visual tab index
  focusedChartId: null, // dock mode: which chart has focus for navbar commands
  activeInteractiveTools: {}, // chartId -> activeInteractiveType
  workspaceLoadProgress: null, // { current, total, symbol } or null when not loading
};

// Helper function to create new chart
export const createNewChart = () => ({
  id: uuidv4(),
  ...defaultChartConfig,
});
